export const takeExam = ({ timeLimitSeconds, hasInfoSlide, exercises }) => ({
  remaining: timeLimitSeconds,
  hasInfoSlide,
  exercises,
  exerciseIds: exercises.map(({ id }) => id),
  currentIndex: hasInfoSlide ? -1 : 0,
  progressIndex: hasInfoSlide ? -1 : 0,
  /** @type {'exercise' | 'exam_info' | 'result_dialog' | 'review_mode'} */
  view: hasInfoSlide ? 'exam_info' : 'exercise',
  timer: null,

  async init() {
    this.exercises.forEach((exercise) => {
      exercise.marked = false
      exercise.selectedAnswer = null
    })

    // Start timer immediately if the exam doesn't have an info slide
    if (!hasInfoSlide) this.startTimer()

    const confirmExitListener = (e) => e.preventDefault()

    // Confirm that the user wants to leave if they try to navigate away while they have an unfinished exam
    this.$root.addEventListener('input', () => window.addEventListener('beforeunload', confirmExitListener), {
      once: true,
    })

    // Remove listeners etc. to avoid them leaking into subsequent pages when using Turbo navigation
    this.$root.closest('form').addEventListener('submit', () => {
      window.removeEventListener('beforeunload', confirmExitListener)
      clearInterval(this.timer)
    })
  },
  startTimer() {
    // Specify target time and get the initial values
    const targetTime = Date.now() + this.remaining * 1000

    // Update the timer every 200 milliseconds. Updating only once per second results in some occasional jitters
    // (seems to depend on when the page is loaded), so use a shorter interval.
    this.timer = setInterval(() => {
      this.remaining = Math.ceil((targetTime - Date.now()) / 1000)
      if (this.remaining < 1) {
        // Set remaining to 0 to prevent negative values on a tab that has been left untouched for a while
        this.remaining = 0
        clearInterval(this.timer)
      }
    }, 200)
  },
  startExam() {
    this.startTimer()
    this.nextQuestion()

    if (!this.exercise.show_topic_info_before) {
      this.view = 'exercise'
    }
  },
  nextQuestion() {
    this.currentIndex++
    this.progressIndex++

    if (this.exercise.show_topic_info_before) {
      this.view = 'topic_info'
    }
  },
  jumpToMarked(direction) {
    const target =
      direction === 'next'
        ? this.markedIndexes.filter((i) => i > this.currentIndex)[0]
        : this.markedIndexes.filter((i) => i < this.currentIndex).at(-1)

    // Jump to target index if it's a valid index (check ">= 0" instead of simply true/false since 0 is a falsy value)
    if (target >= 0) {
      this.currentIndex = target
    }
  },
  jumpToQuestion(questionIdx) {
    if (this.view === 'review_mode') {
      this.currentIndex = questionIdx
    }
  },
  startReview() {
    this.view = 'review_mode'
    this.currentIndex = this.markedIndexes[0] || 0
  },

  get exercise() {
    return this.exercises[this.currentIndex] || this.exercises[0]
  },
  get hours() {
    return Math.floor((this.remaining % (60 * 60 * 24)) / (60 * 60))
  },
  get minutes() {
    return Math.floor((this.remaining % (60 * 60)) / 60)
  },
  get seconds() {
    return this.remaining % 60
  },
  // Format the remaining time as a string (stop before going negative)
  get remainingString() {
    const hoursString = this.hours > 0 ? String(this.hours) + ':' : ''
    const minutesString = this.hours > 0 ? String(this.minutes).padStart(2, '0') : this.minutes
    const secondsString = ':' + String(this.seconds).padStart(2, '0')
    return hoursString + minutesString + secondsString
  },
  get markedIndexes() {
    return this.exercises.reduce((memo, exercise, i) => {
      return exercise.marked ? memo.concat(i) : memo
    }, [])
  },
  get showVehicleIcon() {
    return this.exercise.show_vehicle && ['exercise', 'review_mode'].includes(this.view)
  },
  get prevAvailable() {
    return this.markedIndexes[0] && this.markedIndexes[0] < this.currentIndex
  },
  get nextAvailable() {
    return this.markedIndexes.at(-1) && this.markedIndexes.at(-1) > this.currentIndex
  },
})

// Functionality for hiding/showing full questions when they're so long that they'd overflow their container
export const hideQuestionOverflow = () => ({
  overflowing: false,
  showFullText: false,

  init() {
    this.detectOverflow()

    window.addEventListener('resize', () => this.detectOverflow())

    this.$watch('currentIndex', () => {
      this.showFullText = false
      this.detectOverflow()
    })

    this.$watch('view', () => {
      this.showFullText = false
      this.detectOverflow()
    })
  },

  detectOverflow() {
    // In certain cases, the text may not be rendered yet when this is fired.
    // Push to the end of the stack with `setTimeout` to make sure that the text is rendered first.
    setTimeout(() => {
      const containerHeight = this.$root.offsetHeight
      const questionHeight = this.$refs.question.offsetHeight

      this.overflowing = containerHeight < questionHeight

      if (!this.overflowing) {
        this.showFullText = false
      }
    }, 0)
  },
})
